import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../AuthProvider";
import VoiceMailService from "./services/VoiceMailService";
import {
  FaPhone,
  FaSpinner,
  FaCopy,
  FaCheck,
  FaInfoCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";

const defaultForm = {
  emailSample: "",
  contactName: "",
  contactTitle: "",
  companyName: "",
  contactEmail: "",
  sellerName: "",
  sellerRole: "Sales Development Representative",
  sellerCompany: "ReWorld",
  campaignGoal: "Book a meeting",
};

const VoiceMail = () => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState(defaultForm);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        sellerName:
          prev.sellerName ||
          user.displayName ||
          (user.email ? user.email.split("@")[0] : ""),
        contactEmail: prev.contactEmail,
      }));
    }
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const generateVoiceMail = async (event) => {
    event.preventDefault();

    if (!formData.emailSample.trim()) {
      setError("Email sample is required - we'll extract details from it.");
      toast.error("Please provide an email sample.");
      return;
    }

    setIsGenerating(true);
    setError("");
    setResult(null);
    setCopied(false);

    try {
      const payload = await VoiceMailService.generateVoiceMail(formData);
      setResult(payload);
      toast.success("Voice mail ready!");
    } catch (err) {
      console.error("Voice mail generation failed:", err);
      const message = err.message || "Failed to generate voice mail.";
      setError(message);
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyVoiceMail = () => {
    if (!result?.script) return;

    navigator.clipboard.writeText(result.script).then(
      () => {
        setCopied(true);
        toast.success("Voice mail copied.");
        setTimeout(() => setCopied(false), 2000);
      },
      (clipboardError) => {
        console.error("Clipboard write failed:", clipboardError);
        toast.error("Unable to copy voice mail. Try manually selecting.");
      }
    );
  };

  const resetForm = () => {
    setFormData((prev) => ({
      ...defaultForm,
      sellerName: prev.sellerName,
    }));
    setResult(null);
    setError("");
    setCopied(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FaPhone className="mr-3 text-purple-600" />
            Voice Mail Generator
          </h1>
          <p className="text-gray-600 mt-1 max-w-3xl">
            Drop in the email you just sent and we'll craft a 30-second
            voicemail that follows up on your email. We'll extract prospect
            details from the email - just add the email content and your info.
          </p>
        </div>
      </div>

      <form
        onSubmit={generateVoiceMail}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        <div className="lg:col-span-2 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Sample <span className="text-red-500">*</span>
            </label>
            <textarea
              name="emailSample"
              value={formData.emailSample}
              onChange={handleChange}
              rows={14}
              placeholder="Paste the email you just sent or plan to send..."
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
            <p className="text-xs text-gray-500 mt-2 flex items-center">
              <FaInfoCircle className="mr-2 text-purple-500" />
              We keep the voicemail tight but reference this email so they know
              what to look for.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">
              Prospect Details
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Name <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                placeholder="Jordan Smith (extracted from email if blank)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role / Title <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                name="contactTitle"
                value={formData.contactTitle}
                onChange={handleChange}
                placeholder="Extracted from email if blank"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Extracted from email if blank"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                placeholder="Extracted from email if blank"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">
              SDR / Campaign Context
            </h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Name
              </label>
              <input
                type="text"
                name="sellerName"
                value={formData.sellerName}
                onChange={handleChange}
                placeholder="Alex Rivera"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Role
              </label>
              <input
                type="text"
                name="sellerRole"
                value={formData.sellerRole}
                onChange={handleChange}
                placeholder="Sales Development Representative"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Company
              </label>
              <input
                type="text"
                name="sellerCompany"
                value={formData.sellerCompany}
                onChange={handleChange}
                placeholder="ReWorld"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Goal
              </label>
              <input
                type="text"
                name="campaignGoal"
                value={formData.campaignGoal}
                onChange={handleChange}
                placeholder="Book a meeting"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={isGenerating}
                className={`flex-1 inline-flex justify-center items-center px-4 py-2 rounded-md font-medium ${
                  isGenerating
                    ? "bg-purple-200 text-purple-500 cursor-not-allowed"
                    : "bg-purple-600 text-white hover:bg-purple-700"
                }`}
              >
                {isGenerating ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Crafting voice mail...
                  </>
                ) : (
                  <>
                    <FaPhone className="mr-2" />
                    Generate Voice Mail
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Reset
              </button>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
          </div>
        </div>
      </form>

      {result && (
        <div className="bg-white border border-purple-200 rounded-lg shadow-md p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-purple-800 flex items-center">
              <FaPhone className="mr-2" />
              Ready-to-Leave Voice Mail
            </h2>
            <button
              onClick={copyVoiceMail}
              className="inline-flex items-center px-3 py-2 text-sm rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200"
            >
              {copied ? (
                <>
                  <FaCheck className="mr-2 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <FaCopy className="mr-2" />
                  Copy Script
                </>
              )}
            </button>
          </div>

          <div className="whitespace-pre-line text-gray-800 border border-purple-100 bg-purple-50 rounded-md px-4 py-3">
            {result.script}
          </div>

          {result.callToAction && (
            <div className="text-sm text-purple-800 font-medium">
              CTA Highlight: {result.callToAction}
            </div>
          )}

          {Array.isArray(result.keyPoints) && result.keyPoints.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-purple-700 uppercase tracking-wide">
                Key Reminders
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                {result.keyPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceMail;
